import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Redirect } from 'react-router-dom';
import Cookies from 'universal-cookie';
import axios from 'axios';
import { Button, Form, FormGroup } from 'react-bootstrap';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import { RouterPaths } from '../../../app/appConstants';
import { Loader } from '../../../components/Loader/Loader.jsx';
import FormPasswordEye from '../../../components/FormPasswordEye/FormPasswordEye.jsx';
import { SelectCountry } from '../../../components/SelectCountry/SelectCountry.jsx';
import { useLangContext } from '../../../providers/LangProvider/LangProviderContext.js';
import { getRequest } from '../../../app/request.js';
import { getAvailableCountries } from '../../../app/appSelectors.js';
import { registrationAction } from '../authActions.js';
import { getAppLoggedIn, getAppLoggedInProcess, getAppRegistrationError } from '../authSelectors.js';
import AuthWrapper from './AuthWrapper';
import RegistrationAcceptPolicy from './RegistrationAcceptPolicy.jsx';
import { LoginStyled } from './styled/LoginStyled';

const browserCookies = new Cookies();

interface CountryOption {
    iso: string;
    [key: string]: unknown;
}

interface RegistrationProps {
    availableCountries: CountryOption[];
    loggedIn?: boolean;
    loggedInProcess?: boolean;
    registrationError?: string | boolean | null;
    registrationAction: (payload: Record<string, unknown>) => void;
}

function Registration({
    availableCountries,
    loggedIn,
    loggedInProcess,
    registrationError,
    registrationAction: dispatchRegistration,
}: RegistrationProps) {
    const { getLangPath } = useLangContext();
    const [selectedIso, setSelectedIso] = useState<string | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
    const [pref, setPref] = useState('');
    const [referrerName, setReferrerName] = useState<string | null>(null);

    useEffect(() => {
        setPref(new URLSearchParams(window.location.search).get('pref') || browserCookies.get('pref') || '');
    }, []);

    useEffect(() => {
        const value = pref.trim();
        if (!value) {
            setReferrerName(null);
            return;
        }

        let cancelled = false;
        getRequest('/auth/register/referrer', { pref: value })
            .then((data: { full_name?: string }) => {
                if (!cancelled) {
                    setReferrerName(data.full_name || null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setReferrerName(null);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [pref]);

    const submitHandler = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const form = e.currentTarget;
            const prefInput = form.elements.namedItem('pref') as HTMLInputElement | null;
            const prefValue = prefInput?.value?.trim();
            if (prefValue) {
                browserCookies.set('pref', prefValue, { path: '/', maxAge: 60 * 60 * 24 * 30 });
            }
            const passwordInput = form.elements.namedItem('password') as HTMLInputElement;
            const acceptRulesInput = form.elements.namedItem('accept-rules') as HTMLInputElement;
            const emailInput = form.elements.namedItem('email') as HTMLInputElement;
            const phoneInput = form.elements.namedItem('phone') as HTMLInputElement | null;
            const fullnameInput = form.elements.namedItem('fullname') as HTMLInputElement | null;

            dispatchRegistration({
                password: passwordInput.value,
                acceptRules: acceptRulesInput.checked,
                email: emailInput.value,
                phone: phoneInput?.value,
                country: selectedCountry?.iso,
                fullname: fullnameInput?.value,
                pref: prefValue || undefined,
            });
        },
        [selectedCountry, dispatchRegistration],
    );

    const handleSelectCountry = useCallback((iso: string) => {
        setSelectedIso(iso);
    }, []);

    useEffect(() => {
        axios
            .get('https://api.country.is/')
            .then((geo) => {
                setSelectedIso(geo.data?.country);
            })
            .catch(() => {
                // nothing
            });
    }, []);

    useEffect(() => {
        if (availableCountries.length && selectedIso) {
            const found = availableCountries.find((item) => item.iso === selectedIso.toLowerCase());
            if (found) {
                setSelectedCountry(found);
            }
        }
    }, [availableCountries, selectedIso]);

    if (loggedIn) {
        return <Redirect to={RouterPaths.main} />;
    }

    return (
        <AuthWrapper>
            <LoginStyled $wide>
                <p className="section-eyebrow">{getLangPath('auth.brand')}</p>
                <h1 className="auth-title">{getLangPath('auth.title_registration')}</h1>
                <p className="auth-subtitle">{getLangPath('auth.subtitle_registration')}</p>

                <Form onSubmit={submitHandler}>
                    <FormGroup className="mb-3">
                        <SelectCountry
                            availableCountries={availableCountries}
                            onSelect={handleSelectCountry}
                            selectedCountry={selectedCountry}
                            error={null}
                        />
                    </FormGroup>

                    <Form.Group className="mb-3" controlId="register-email">
                        <Form.Label>{getLangPath('auth.field_email')}</Form.Label>
                        <Form.Control
                            name="email"
                            type="email"
                            placeholder={getLangPath('auth.field_email')}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="register-fullname">
                        <Form.Label>{getLangPath('auth.field_fullname')}</Form.Label>
                        <Form.Control
                            name="fullname"
                            type="text"
                            placeholder={getLangPath('auth.field_fullname')}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="register-phone">
                        <Form.Label>{getLangPath('auth.field_phone')}</Form.Label>
                        <Form.Control
                            name="phone"
                            type="text"
                            placeholder={getLangPath('auth.field_phone')}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="register-partner-code">
                        <Form.Label>{getLangPath('auth.field_partner_code')}</Form.Label>
                        <Form.Control
                            name="pref"
                            type="text"
                            inputMode="numeric"
                            placeholder={getLangPath('auth.field_partner_code')}
                            value={pref}
                            onChange={(e) => setPref(e.target.value)}
                        />
                        {referrerName && (
                            <Form.Text>
                                {getLangPath('auth.referrer_invited_by', { name: referrerName })}
                            </Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="register-password">
                        <Form.Label>{getLangPath('auth.field_password')}</Form.Label>
                        <FormPasswordEye
                            name="password"
                            placeholder={getLangPath('auth.field_password')}
                            autoComplete="off"
                            required
                        />
                    </Form.Group>

                    {!!registrationError && (
                        <p className="auth-error">
                            {getLangPath(
                                typeof registrationError === 'string'
                                    ? registrationError
                                    : 'error.default_message',
                            )}
                        </p>
                    )}

                    <RegistrationAcceptPolicy className="mb-4 mt-4" />

                    <div className="auth-actions">
                        <Button variant="primary" size="lg" type="submit">
                            {getLangPath('auth.registration_submit')}
                        </Button>
                        <Button variant="outline-info" className="btn-md" as={Link} to={RouterPaths.login}>
                            {getLangPath('auth.link_to_login')}
                        </Button>
                    </div>
                </Form>
                <Loader show={!!loggedInProcess} bg={undefined} size={undefined} />
            </LoginStyled>
        </AuthWrapper>
    );
}

const mapStateToProps = createStructuredSelector({
    availableCountries: getAvailableCountries,
    loggedIn: getAppLoggedIn,
    loggedInProcess: getAppLoggedInProcess,
    registrationError: getAppRegistrationError,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    registrationAction: (payload: Record<string, unknown>) => dispatch(registrationAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Registration);
